if (!customElements.get('delivery-checker')) {
  customElements.define(
    'delivery-checker',
    class DeliveryChecker extends HTMLElement {
      static STORAGE_KEY = 'x-delivery-checker-zip';

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="zip"]');
        this.submitButton = this.form.querySelector('button[type="submit"]');
        this.spinner = this.submitButton.querySelector('.loading__spinner');
        this.clearButton = this.querySelector('[data-clear]');
        this.states = this.querySelectorAll('[data-state]');
        this.eyebrows = this.querySelectorAll('[data-eyebrow]');
        this.deadlineSlot = this.querySelector('[data-deadline]');
        this.deadlineWords = this.deadlineSlot.dataset.words.split('|');
        this.chips = this.querySelectorAll('[data-preset-zip]');
        this.resultPanel = this.querySelector('.x-delivery-checker__panel--result');

        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.input.addEventListener('input', this.onInput.bind(this));
        this.clearButton.addEventListener('click', this.onClear.bind(this));
        this.chips.forEach((chip) =>
          chip.addEventListener('click', () => this.onChip(chip))
        );

        const saved = this.readSavedZip();
        if (saved) {
          this.setZip(saved);
          this.check(saved, { silent: true });
        }
      }

      // Formats the input ("760 01"), toggles the clear button and highlights
      // the matching city chip — the three always change together.
      setZip(digits) {
        this.input.value =
          digits.length > 3 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : digits;
        this.clearButton.hidden = digits.length === 0;
        this.chips.forEach((chip) => {
          chip.setAttribute(
            'aria-pressed',
            String(chip.dataset.presetZip.replace(/\D/g, '') === digits)
          );
        });
      }

      setLoading(on) {
        this.submitButton.classList.toggle('loading', on);
        this.spinner.classList.toggle('hidden', !on);
      }

      setEyebrow(variant) {
        this.eyebrows.forEach((span) => {
          span.hidden = span.dataset.eyebrow !== variant;
        });
      }

      showState(name) {
        this.states.forEach((el) => {
          el.hidden = el.dataset.state !== name;
        });
        // 'available' sets its own deadline eyebrow in renderAvailable.
        if (name && name !== 'available') this.setEyebrow('idle');
      }

      onInput() {
        const digits = this.input.value.replace(/\D/g, '').slice(0, 5);
        this.setZip(digits);
        if (digits.length === 5 && digits !== this.lastZip) this.check(digits);
      }

      onSubmit(event) {
        event.preventDefault();
        const digits = this.input.value.replace(/\D/g, '');
        if (digits.length !== 5) {
          this.showState('invalid');
          this.revealResult();
          this.input.focus();
          return;
        }
        this.check(digits);
      }

      // With the panels stacked (mobile), the result is often below the fold
      // while typing — bring it into view. 'nearest' makes this a no-op when
      // it is already visible (e.g. desktop side-by-side layout).
      revealResult() {
        this.resultPanel.scrollIntoView({
          behavior: matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'nearest',
        });
      }

      onChip(chip) {
        const digits = chip.dataset.presetZip.replace(/\D/g, '');
        this.setZip(digits);
        if (digits !== this.lastZip) this.check(digits);
      }

      onClear() {
        this.abortController?.abort();
        this.lastZip = null;
        this.setZip('');
        this.setLoading(false);
        this.showState('idle');
        try {
          localStorage.removeItem(DeliveryChecker.STORAGE_KEY);
        } catch {}
        this.input.focus();
      }

      async check(zip, { silent = false } = {}) {
        this.lastZip = zip;
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        this.setLoading(true);
        this.showState(null);

        try {
          const response = await fetch(`${this.dataset.endpoint}?zip=${zip}`, {
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();

          if (data.available) {
            this.renderAvailable(data);
            this.showState('available');
            this.saveZip(zip);
          } else {
            this.showState('unavailable');
          }
          if (!silent) this.revealResult();
        } catch (error) {
          if (error.name === 'AbortError') return;
          // A silent check is the automatic re-check of a stored ZIP on page
          // load — a network hiccup there should not surface an error the
          // visitor never asked about (and must never scroll the page).
          this.showState(silent ? 'idle' : 'error');
          if (!silent) this.revealResult();
        } finally {
          if (this.abortController === controller) this.setLoading(false);
        }
      }

      renderAvailable(data) {
        const date = this.parseDate(data.expectedDeliveryDate);
        const lang = document.documentElement.lang || 'cs';
        this.querySelector('[data-weekday]').textContent = new Intl.DateTimeFormat(
          lang,
          { weekday: 'long' }
        ).format(date);
        this.querySelector('[data-daymonth]').textContent = new Intl.DateTimeFormat(
          lang,
          { day: 'numeric', month: 'long' }
        ).format(date);

        const days = Array.isArray(data.days) ? data.days : [];
        this.querySelectorAll('[data-state="available"] [data-day]').forEach(
          (pill) => {
            pill.classList.toggle('on', days.includes(pill.dataset.day));
          }
        );
        this.querySelector('[data-days-text]').textContent =
          data.deliveryDaysString || '';

        if (data.orderDeadline) {
          const word = this.deadlineWords[this.deadlineWordIndex(this.parseDate(data.orderDeadline))];
          this.deadlineSlot.textContent = data.cutoff ? `${word} ${data.cutoff}` : word;
          this.setEyebrow('deadline');
        } else {
          this.setEyebrow('idle');
        }
      }

      // Local-time parse of the API's "YYYY-MM-DD". `new Date(iso)` would be
      // UTC midnight and shift the calendar day for UTC-negative visitors.
      parseDate(iso) {
        const [y, m, d] = iso.split('-').map(Number);
        return new Date(y, m - 1, d);
      }

      // Presentation only — the deadline itself comes from the API. Picks the
      // localized word for it: index into the deadline_days locale list
      // (today|tomorrow|Mon..Fri), relative to the Prague calendar.
      deadlineWordIndex(deadline) {
        const prague = new Date(
          new Date().toLocaleString('en-US', { timeZone: 'Europe/Prague' })
        );
        const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
        const diffDays = Math.round(
          (startOfDay(deadline) - startOfDay(prague)) / 864e5
        );

        if (diffDays <= 0) return 0;
        if (diffDays === 1) return 1;
        return deadline.getDay() + 1; // Mon(1)..Fri(5) -> index 2..6
      }

      readSavedZip() {
        try {
          const value = localStorage.getItem(DeliveryChecker.STORAGE_KEY);
          return /^\d{5}$/.test(value) ? value : null;
        } catch {
          return null;
        }
      }

      saveZip(zip) {
        try {
          localStorage.setItem(DeliveryChecker.STORAGE_KEY, zip);
        } catch {
          // Storage unavailable (private mode, blocked) — recall is a nicety.
        }
      }
    }
  );
}
